import React from 'react';
import SearchInput from './SearchInput';
import Button from './ui/Button';
import { buttonClassName } from './ui/buttonStyles';
import { Select } from './ui/Field';
import { ETAPAS } from '../utils/emendaEtapa';

interface EmendasFilterBarProps {
  query: string;
  onQueryChange: (v: string) => void;
  etapaFilter: string;
  onEtapaFilterChange: (v: string) => void;
  /** Oculta o seletor de etapa (quando a página oferece esse filtro em outro lugar). */
  hideEtapa?: boolean;
  /** Filtro de tipo de transferência (Direta/Indireta) — só renderiza se o handler existir. */
  transferencia?: string;
  onTransferenciaChange?: (v: string) => void;
  /** Filtro de esfera (Municipal/Estadual/Federal) — só renderiza se o handler existir. */
  esfera?: string;
  onEsferaChange?: (v: string) => void;
  /** Filtro de devolução à Câmara — só renderiza se o handler existir. */
  camara?: string;
  onCamaraChange?: (v: string) => void;
  year: string;
  onYearChange: (v: string) => void;
  areaFilter?: string;
  onAreaFilterChange?: (v: string) => void;
  areas?: { value: string; label: string }[];
  anos?: string[];
  vereador?: string;
  onVereadorChange?: (v: string) => void;
  vereadores?: string[];
  canFilterByVereador?: boolean;
  secretaria?: string;
  onSecretariaChange?: (v: string) => void;
  secretarias?: string[];
  canFilterBySecretaria?: boolean;
  myQueueOnly: boolean;
  onMyQueueToggle: () => void;
  myQueueCount: number;
  myQueueStatuses: string[];
  isQueueRestricted: boolean;
  onClearFilters: () => void;
}

// Largura mínima pelo maior rótulo do seletor ("Todas as transferências", nomes de secretaria):
// abaixo disso o navegador corta o valor selecionado sem reticências.
const FILTER_SELECT = 'min-w-[14rem] flex-1';

const QueueCount: React.FC<{ count: number; active: boolean }> = ({ count, active }) => (
  <span
    className={`inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1 text-xs font-bold ${
      active ? 'bg-primary-fg text-primary' : 'bg-primary text-primary-fg'
    }`}
  >
    {count}
  </span>
);

const EmendasFilterBar: React.FC<EmendasFilterBarProps> = ({
  query,
  onQueryChange,
  etapaFilter,
  onEtapaFilterChange,
  year,
  onYearChange,
  areaFilter = '',
  onAreaFilterChange,
  areas = [],
  anos,
  vereador = '',
  onVereadorChange,
  vereadores = [],
  canFilterByVereador = false,
  secretaria = '',
  onSecretariaChange,
  secretarias = [],
  canFilterBySecretaria = false,
  myQueueOnly,
  onMyQueueToggle,
  myQueueCount,
  myQueueStatuses,
  isQueueRestricted,
  onClearFilters,
  hideEtapa = false,
  transferencia = '',
  onTransferenciaChange,
  esfera = '',
  onEsferaChange,
  camara = '',
  onCamaraChange,
}) => {
  const anoOptions = anos ?? ['2026', '2025', '2024', '2023', '2022'];

  return (
    <div className="flex flex-wrap gap-3">
      <div className="min-w-[16rem] flex-[3]">
        <SearchInput
          value={query}
          onChange={onQueryChange}
          placeholder="Buscar emenda..."
          ariaLabel="Buscar por descrição, código, instituição ou parlamentar"
        />
      </div>

      {canFilterByVereador && onVereadorChange && (
        <Select fieldWidth="auto" value={vereador} onChange={(e) => onVereadorChange(e.target.value)} className={FILTER_SELECT}>
          <option value="">Todos os vereadores</option>
          {vereadores.map((v) => <option key={v} value={v}>{v}</option>)}
        </Select>
      )}

      {canFilterBySecretaria && onSecretariaChange && (
        <Select fieldWidth="auto" value={secretaria} onChange={(e) => onSecretariaChange(e.target.value)} className={FILTER_SELECT}>
          <option value="">Todas as secretarias</option>
          {secretarias.map((s) => <option key={s} value={s}>{s}</option>)}
        </Select>
      )}

      {!hideEtapa && (
        <Select fieldWidth="auto" value={etapaFilter} onChange={(e) => onEtapaFilterChange(e.target.value)} className={FILTER_SELECT}>
          <option value="Todas">Todas as etapas</option>
          {ETAPAS.map((e) => <option key={e} value={e}>{e}</option>)}
        </Select>
      )}

      {onTransferenciaChange && (
        <Select fieldWidth="auto" value={transferencia} onChange={(e) => onTransferenciaChange(e.target.value)} className={FILTER_SELECT}>
          <option value="">Todas as transferências</option>
          <option value="Direta">Direta</option>
          <option value="Indireta">Indireta</option>
        </Select>
      )}

      {onEsferaChange && (
        <Select fieldWidth="auto" value={esfera} onChange={(e) => onEsferaChange(e.target.value)} className={FILTER_SELECT}>
          <option value="">Todas as esferas</option>
          <option value="Municipal">Municipal</option>
          <option value="Estadual">Estadual</option>
          <option value="Federal">Federal</option>
        </Select>
      )}

      {onCamaraChange && (
        <Select fieldWidth="auto" value={camara} onChange={(e) => onCamaraChange(e.target.value)} className={FILTER_SELECT}>
          <option value="">Todas (Câmara)</option>
          <option value="EM_APROVACAO">Em aprovação na Câmara</option>
          <option value="AGUARDANDO_DEVOLUCAO">Aguardando devolução à Câmara</option>
          <option value="DEVOLVIDA">Devolvidas à Câmara</option>
        </Select>
      )}

      {onAreaFilterChange && (
        <Select fieldWidth="auto" value={areaFilter} onChange={(e) => onAreaFilterChange(e.target.value)} className={FILTER_SELECT}>
          <option value="">Todas as áreas</option>
          {areas.map((area) => <option key={area.value} value={area.value}>{area.label}</option>)}
        </Select>
      )}

      <Select fieldWidth="auto" value={year} onChange={(e) => onYearChange(e.target.value)} className="min-w-[10rem]">
        <option value="">Todos os anos</option>
        {anoOptions.map((a) => <option key={a} value={a}>{a}</option>)}
      </Select>

      <div className="flex items-center gap-2">
        {isQueueRestricted ? (
          <span className={buttonClassName({ variant: 'primary' })}>
            Minha Fila
            {myQueueCount > 0 && <QueueCount count={myQueueCount} active />}
          </span>
        ) : myQueueStatuses.length > 0 ? (
          <Button variant={myQueueOnly ? 'primary' : 'secondary'} onClick={onMyQueueToggle} aria-pressed={myQueueOnly}>
            Minha Fila
            {myQueueCount > 0 && <QueueCount count={myQueueCount} active={myQueueOnly} />}
          </Button>
        ) : null}

        <Button variant="secondary" onClick={onClearFilters}>Limpar filtros</Button>
      </div>
    </div>
  );
};

export default EmendasFilterBar;
